import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Heart, ShieldCheck, Clock } from 'lucide-react';
import { IMAGES, ABOUT_VALUES } from '../data/mock';

const ICONS = { Sparkles, Heart, ShieldCheck, Clock };

const About = () => {
  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="relative bg-[#0f1113] text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-90"
          style={{ backgroundImage: `url(${IMAGES.executiveConsult})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f1113] via-[#0f1113]/85 to-transparent" />
        <div className="relative max-w-[1280px] mx-auto px-6 py-24 md:py-28">
          <div className="max-w-xl">
            <p className="text-[11px] tracking-[0.3em] text-[#c9a961] mb-6 font-medium">ABOUT AURELIA</p>
            <h1 className="font-serif text-5xl md:text-6xl leading-[1.05] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              Redefining <br />Wellness. Delivered.
            </h1>
            <p className="text-white/80 text-base leading-relaxed max-w-md">
              Aurelia was founded on a simple belief: exceptional healthcare should be personal, private, and delivered on your schedule.
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="bg-[#f5f0ea] py-20 md:py-24">
        <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-5">OUR STORY</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.1] text-[#1c1e20] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              A Higher Standard <br />of Concierge Care.
            </h2>
            <div className="w-16 h-[2px] bg-[#c9a961] mb-6" />
            <div className="space-y-5 text-[#4a4a4a] leading-relaxed text-[15px]">
              <p>
                Aurelia Concierge Wellness Partners was created to close the gap between clinical excellence and the calm, curated experience today’s patients deserve. From executives with impossible calendars to families that value privacy and comfort, we bring elevated care to the places our clients call home.
              </p>
              <p>
                Every appointment is delivered by licensed, insured professionals who move with the discretion of a private concierge and the precision of a hospital laboratory. We handle every detail so you can focus on what matters most — your health, your family, and your time.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src="/generated/executive_consult.png" alt="Consult" className="w-full h-72 object-cover" />
            <img src="/generated/luxury_interior.png" alt="Home" className="w-full h-72 object-cover mt-10" />
            <img src="/generated/vials_tray.png" alt="Lab" className="w-full h-72 object-cover -mt-6" />
            <img src="/generated/team_portrait.png" alt="Team" className="w-full h-72 object-cover mt-4" />
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-[900px] mx-auto px-6">
          <div className="mb-10 flex justify-center">
            <img
              src="/brand/founder.png?v=2"
              alt="Ryleigh Davidson, CPT — Founder and CEO of Aurelia"
              className="w-full max-w-[420px] h-auto object-cover shadow-sm"
              data-testid="founder-portrait"
            />
          </div>
          <div className="text-center">
            <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-5">MEET THE FOUNDER</p>
            <h2
              className="font-serif text-4xl md:text-5xl leading-[1.1] text-[#1c1e20] mb-1"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
            >
              Ryleigh Davidson, CPT
            </h2>
            <p className="text-[13px] tracking-[0.24em] text-[#8a7132] font-semibold mb-4">
              FOUNDER &amp; CEO
            </p>
            <div className="w-16 h-[2px] bg-[#c9a961] mx-auto mb-7" />
          </div>
          <p
            className="text-[#4a4a4a] leading-[1.85] text-[15px] max-w-[720px] mx-auto"
            data-testid="founder-bio"
          >
            Ryleigh Davidson, CPT, is the Founder and CEO of Aurelia, bringing 8+ years of experience
            in laboratory and patient-care services. Throughout her career, she has worked with
            patients across the lifespan&mdash;from newborns to geriatric adults&mdash;in hospitals,
            healthcare facilities, private-client settings, and research institutions. Known for her
            professionalism, precision, and patient-centered approach, Ryleigh founded Aurelia to
            provide a more convenient, discreet, and elevated approach to specimen collection and
            wellness services. Her mission is to make quality healthcare experiences more accessible
            while delivering the level of service, comfort, and attention to detail every client
            deserves.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-5">OUR VALUES</p>
            <h2 className="font-serif text-4xl text-[#1c1e20] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              What Guides Every Visit.
            </h2>
            <div className="w-16 h-[2px] bg-[#c9a961] mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {ABOUT_VALUES.map((v) => {
              const Icon = ICONS[v.icon];
              return (
                <div key={v.title} className="border border-black/10 p-8 text-center hover:border-[#c9a961] transition-colors duration-300 group">
                  <div className="w-14 h-14 rounded-full border border-[#c9a961] flex items-center justify-center mx-auto mb-5 group-hover:bg-[#c9a961] transition-colors">
                    <Icon size={22} className="text-[#c9a961] group-hover:text-white transition-colors" strokeWidth={1.4} />
                  </div>
                  <h3 className="font-serif text-2xl text-[#1c1e20] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>{v.title}</h3>
                  <p className="text-[#4a4a4a] text-[14px] leading-relaxed">{v.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-[#141618] text-white">
        <div className="max-w-[1280px] mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-2 items-center gap-8">
          <h3 className="font-serif text-3xl md:text-4xl leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
            Care as refined as <br />the life you’ve built.
          </h3>
          <div>
            <p className="text-white/80 mb-5 max-w-md">
              Discover what concierge wellness feels like when every detail is considered.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-[#c9a961] hover:bg-[#b89751] transition-colors duration-300 text-[#141618] tracking-[0.22em] text-[12px] font-semibold px-7 py-3.5"
            >
              BOOK YOUR APPOINTMENT
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
